import React from "react";
import PropTypes from "prop-types";
import Text from "../RTE/Text";
import { graphql, useStaticQuery } from "gatsby";
import ErrorComponent from "@components/Error";
import openQuotesDark from "@images/icons/open-quotes-dark.svg";
import closeQuotesDark from "@images/icons/close-quotes-dark.svg";
import openQuotesLight from "@images/icons/open-quotes.svg";
import closeQuotesLight from "@images/icons/close-quotes.svg";

/**
 * Quote Component - Displays content in a styled blockquote
 *
 * This component wraps content (typically a Text component) in a styled blockquote.
 * It accepts a Text component or content that will be passed to a Text component.
 *
 * @param {Object} props - Component props
 * @param {Object|string|React.ReactElement} props.content - Content to display (a Text component, rich text object, or string)
 * @param {string} props.style - Additional CSS classes for styling the parent container
 * @param {string} props.variant - Style variant to use ('dark' or 'light')
 * @param {Function} props.onError - Optional callback for error handling
 * @returns {React.ReactElement} Rendered quote component
 */
const Quote = ({
  content,
  style = "",
  type = "",
  onError
}) => {
  // Define style variants
  const styles = {
    "Dark Green": "bg-dark_green",
	"Soft Green": "bg-soft_green",
	"Soft Blue": "bg-soft_blue",
    "Soft Orange": "bg-[#FBE1D0]",
    "Soft Green - 30": "bg-[#D4EADC]",
  };

  // Get the appropriate blockquote style
	const blockquoteStyle = `relative ${type === "Dark Green" ? "py-[48px]" : "py-[32px]"} lg:py-[46px] lg:px-[56px] text-center rounded-all_48 ${styles[type] || styles["Dark Green"]} ${style}`.trim();

  // Fetch sample data if content is "sample"
  const sampleData = useStaticQuery(graphql`
    query QuoteSampleQuery {
      contentfulQuote(entryTitle: {eq: "SAMPLE | Quote | Dark Green"}) {
        id
		classOverlay {
			classOverlay
		}
		items {
			... on ContentfulRichText {
				__typename
				id
					text {
					raw
					}
			}
		}
      }
    }
  `);

  // Handle errors
  const handleError = (message, error) => {
    console.error(message, error);
    if (onError) onError(message, error);
    return <ErrorComponent message={message} />;
  };

  // Determine what content to render
  const renderContent = () => {
    try {
      // If content is "sample", use sample data
      if (content === "sample") {
        const sampleContent = sampleData?.contentfulQuote?.items;

        if (!sampleContent.length) {
          return handleError("Sample content not found");
        }

        // Parse and render the sample content
		  return (
			sampleContent.map((item) => {
				return (
					<Text
						content={item?.text?.raw}
						classOverlay={item?.classOverlay?.classOverlay}
						marginTopDesktop={item?.marginTopDesktop}
						marginBottomDesktop={item?.marginBottomDesktop}
						marginTopMobile={item?.marginTopMobile}
						marginBottomMobile={item?.marginBottomMobile}
						paddingTopDesktop={item?.paddingTopDesktop}
						paddingBottomDesktop={item?.paddingBottomDesktop}
						paddingTopMobile={item?.paddingTopMobile}
						paddingBottomMobile={item?.paddingBottomMobile}
						paddingLeftDesktop={item?.paddingLeftDesktop}
						paddingRightDesktop={item?.paddingRightDesktop}
						paddingLeftMobile={item?.paddingLeftMobile}
						paddingRightMobile={item?.paddingRightMobile}
						maxWidthDesktop={item?.maxWidthDesktop}
						maxWidthMobile={item?.maxWidthMobile}
						textAlign={item?.textAlign}
						textColor={item?.textColor?.textColor}
						references={item?.references}
						style={item?.style}
						nodeStyles={item?.nodeStyles}
						customRenderers={item?.customRenderers}
						customMarks={item?.customMarks}
						onError={onError}
					/>
				)
			})
        );
      }

      // For null or undefined content, show an error
      if (content === null || content === undefined || !content.length) {
        return handleError("No content provided to Quote component");
      }

      // Otherwise, render using the Text component with default quote styling
      return (
        content.map((item) => {
			return (
				<Text
					key={item?.id}
					content={item?.text?.raw}
					classOverlay={`${item?.classOverlay?.classOverlay} ${item?.textAlign === "Center" ? "mx-auto" : ""}`}
					marginTopDesktop={item?.marginTopDesktop}
					marginBottomDesktop={item?.marginBottomDesktop}
					marginTopMobile={item?.marginTopMobile}
					marginBottomMobile={item?.marginBottomMobile}
					paddingTopDesktop={item?.paddingTopDesktop}
					paddingBottomDesktop={item?.paddingBottomDesktop}
					paddingTopMobile={item?.paddingTopMobile}
					paddingBottomMobile={item?.paddingBottomMobile}
					paddingLeftDesktop={item?.paddingLeftDesktop}
					paddingRightDesktop={item?.paddingRightDesktop}
					paddingLeftMobile={item?.paddingLeftMobile}
					paddingRightMobile={item?.paddingRightMobile}
					maxWidthDesktop={item?.maxWidthDesktop}
					maxWidthMobile={item?.maxWidthMobile}
					textAlign={item?.textAlign}
					textColor={item?.textColor?.textColor}
					references={item?.references}
					style={item?.style}
					nodeStyles={item?.nodeStyles}
					customRenderers={item?.customRenderers}
					customMarks={item?.customMarks}
					onError={onError}
				/>
			)
		})
      );
    } catch (error) {
      return handleError(`Error rendering quote content: ${error.message}`, error);
    }
  };

  // Render the quote component with appropriate quote images
  return (
    <blockquote className={`quote ${blockquoteStyle}`}>
      {type === 'Dark Green' && (
        <>
          {/* Open quotes - dark variant */}
          <img
            src={openQuotesDark}
            alt="Open quote"
					  className="absolute top-[30px] left-[20px] opacity-[0.7] lg:opacity-[1] lg:top-[48px] lg:left-[48px] w-16 h-auto z-[0] open-quotes"
            aria-hidden="true"
          />

          {/* Content */}
				  <div className="relative z-[1]">
            {renderContent()}
          </div>

          {/* Close quotes - dark variant */}
          <img
            src={closeQuotesDark}
            alt="Close quote"
					  className="absolute opacity-[0.7] lg:opacity-[1] lg:bottom-[48px] lg:right-[48px] bottom-[30px] right-[20px] w-16 h-auto z-0 close-quotes"
            aria-hidden="true"
          />
        </>
      )}

      {type === 'Soft Green' && (
        <>
          {/* Open quotes - dark variant */}
          <img
            src={openQuotesLight}
            alt="Open quote"
            className="absolute top-[90px] left-[20px] opacity-[0.7] lg:opacity-[1] lg:top-[48px] lg:left-[48px] w-16 h-auto z-[0] open-quotes"
            aria-hidden="true"
          />

          {/* Content */}
          <div className="relative z-[1]">
            {renderContent()}
          </div>

          {/* Close quotes - dark variant */}
          <img
            src={closeQuotesLight}
            alt="Close quote"
            className="absolute opacity-[0.7] lg:opacity-[1] lg:bottom-[48px] lg:right-[48px] bottom-[60px] right-[12px] w-16 h-auto z-0 close-quotes"
            aria-hidden="true"
          />
        </>
      )}

      {type === 'Soft Blue' && (
        <>
          {/* Open quotes - dark variant */}
          <img
            src={openQuotesLight}
            alt="Open quote"
            className="absolute top-[90px] left-[20px] opacity-[0.7] lg:opacity-[0.4] lg:top-[48px] lg:left-[48px] w-16 h-auto z-[0] open-quotes"
            aria-hidden="true"
          />

          {/* Content */}
          <div className="relative z-[1]">
            {renderContent()}
          </div>

          {/* Close quotes - dark variant */}
          <img
            src={closeQuotesLight}
            alt="Close quote"
            className="absolute opacity-[0.7] lg:opacity-[0.4] lg:bottom-[48px] lg:right-[48px] bottom-[80px] right-[12px] w-16 h-auto z-0 close-quotes"
            aria-hidden="true"
          />
        </>
      )}

      {type === 'Soft Orange' && (
        <>
          {/* Open quotes - light variant */}
          <img
            src={openQuotesLight}
            alt="Open quote"
            className="absolute top-[90px] left-[20px] opacity-[0.7] lg:opacity-[1] lg:top-[48px] lg:left-[48px] w-16 h-auto z-[0] open-quotes"
            aria-hidden="true"
          />

          {/* Content */}
          <div className="relative z-[1]">
            {renderContent()}
          </div>

          {/* Close quotes - light variant */}
          <img
            src={closeQuotesLight}
            alt="Close quote"
            className="absolute opacity-[0.7] lg:opacity-[1] lg:bottom-[48px] lg:right-[48px] bottom-[60px] right-[12px] w-16 h-auto z-0 close-quotes"
            aria-hidden="true"
          />
        </>
      )}

      {type === 'Soft Green - 30' && (
        <>
          {/* Open quotes - light variant */}
          <img
            src={openQuotesLight}
            alt="Open quote"
            className="absolute top-[90px] left-[20px] opacity-[0.7] lg:opacity-[0.4] lg:top-[48px] lg:left-[48px] w-16 h-auto z-[0] open-quotes"
            aria-hidden="true"
          />

          {/* Content */}
          <div className="relative z-[1]">
            {renderContent()}
          </div>

          {/* Close quotes - light variant */}
          <img
            src={closeQuotesLight}
            alt="Close quote"
            className="absolute opacity-[0.7] lg:opacity-[0.4] lg:bottom-[48px] lg:right-[48px] bottom-[60px] right-[12px] w-16 h-auto z-0 close-quotes"
            aria-hidden="true"
          />
        </>
      )}
    </blockquote>
  );
};

Quote.propTypes = {
  content: PropTypes.oneOfType([
    PropTypes.string,
    PropTypes.object,
    PropTypes.element
  ]).isRequired,
  style: PropTypes.string,
  variant: PropTypes.oneOf(['dark', 'light']),
  onError: PropTypes.func
};

export default Quote;
